import { logger, useBeforeunload } from '@sd/shared';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

const isElement = (thing: any): thing is Element => typeof thing === 'object' && 'tagName' in thing;

const isHTMLInputElement = (element: Element) => element.tagName === 'INPUT';

const isHTMLInputButton = (element: Element): element is HTMLInputElement =>
  isHTMLInputElement(element) && ['submit', 'button'].includes(String(element.getAttribute('type')?.toLowerCase()));

const isHTMLButton = (element: Element): element is HTMLInputElement | HTMLButtonElement =>
  element.tagName === 'BUTTON';

const isHTMLAnchor = (element: Element): element is HTMLAnchorElement => element.tagName === 'A';

const isBootstrapButton = (element: Element) =>
  // We have to check if the className is a string because SVG elements can have
  // objects instead of strings as their className properties
  typeof element.className === 'string' && /\bbtn\b/.test(element.className);

const isLoggableElement = (element: Element) =>
  isHTMLButton(element) || isHTMLAnchor(element) || isHTMLInputButton(element) || isBootstrapButton(element);

const findLoggableParent = (element: Element) => {
  let currentElement: Element | null = element;
  while (currentElement) {
    if (isLoggableElement(currentElement)) return currentElement;
    currentElement = currentElement.parentElement;
  }
};

/**
 * Automatically logs certain global events like:
 * * User clicks a link
 * * User clicks a button
 * * Route navigation
 * * App boot up
 * * User leaves the app
 */
export const AutomaticEventLogger = () => {
  const location = useLocation();
  const [lastLocationPathname, setLastLocationPathname] = useState<string>();
  //////////////////////////
  // Log when the app boots up
  //////////////////////////
  useEffect(() => {
    logger.info('app:boot');
  }, []);

  useBeforeunload(() => {
    logger.info('app:unload');
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    logger.flush();
  });

  //////////////////////////
  // Log when we navigate
  //////////////////////////
  useEffect(() => {
    logger.info('app:navigate', { from: lastLocationPathname, to: location.pathname });
    setLastLocationPathname(location.pathname);
  }, [location.pathname]);

  //////////////////////////
  // Log when we click stuff
  //////////////////////////
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (isElement(e.target)) {
        const loggableElement = findLoggableParent(e.target);
        if (loggableElement) {
          const elementType = isHTMLAnchor(loggableElement) ? 'link' : 'button';
          const logData = {
            text: loggableElement.textContent || loggableElement.getAttribute('value') || undefined,
            href: loggableElement.getAttribute('href') || undefined,
            id: loggableElement.getAttribute('id') || undefined,
            'data-testid': loggableElement.getAttribute('data-testid') || undefined
          };
          logger.info(`${elementType}:click`, logData);
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          logger.flush();
        }
      }
    };

    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  });
  return null;
};
